const CACHE_KEY = 'rain-il-forecast-v1';
const CACHE_TTL_MS = 45 * 60 * 1000;

/** Israel grid ~0.25° — keeps Open-Meteo cost under ~100 locations. */
export const GRID = (() => {
  const lats = [];
  const lons = [];
  for (let lat = 29.5; lat <= 33.5 + 1e-9; lat += 0.25) lats.push(+lat.toFixed(2));
  for (let lon = 34.25; lon <= 35.75 + 1e-9; lon += 0.25) lons.push(+lon.toFixed(2));
  /** @type {number[]} */
  const flatLat = [];
  /** @type {number[]} */
  const flatLon = [];
  for (const lat of lats) {
    for (const lon of lons) {
      flatLat.push(lat);
      flatLon.push(lon);
    }
  }
  return { lats, lons, flatLat, flatLon, nLat: lats.length, nLon: lons.length };
})();

/**
 * @typedef {{
 *   times: string[],
 *   timesUnix: number[],
 *   flatLat: number[],
 *   flatLon: number[],
 *   nLat: number,
 *   nLon: number,
 *   precipitation: number[][],
 *   temperature_2m: number[][],
 *   wind_speed_10m: number[][],
 *   wind_direction_10m: number[][],
 *   fetchedAt: number,
 * }} ForecastGrid
 */

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.fetchedAt || Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return /** @type {ForecastGrid} */ (parsed);
  } catch {
    return null;
  }
}

function writeCache(grid) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(grid));
  } catch {
    /* quota — ignore */
  }
}

export async function fetchForecastGrid(force = false) {
  if (!force) {
    const hit = readCache();
    if (hit) return hit;
  }
  const params = new URLSearchParams({
    latitude: GRID.flatLat.join(','),
    longitude: GRID.flatLon.join(','),
    hourly: 'precipitation,temperature_2m,wind_speed_10m,wind_direction_10m',
    forecast_days: '2',
    timezone: 'Asia/Jerusalem',
    models: 'ecmwf_ifs',
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
  if (!res.ok) throw new Error(`Open-Meteo ${res.status}`);
  const payload = await res.json();
  const rows = Array.isArray(payload) ? payload : [payload];
  if (!rows.length) throw new Error('Open-Meteo empty');

  const times = rows[0].hourly?.time || [];
  const timesUnix = times.map((iso) => Math.floor(new Date(iso).getTime() / 1000));
  const nT = times.length;
  const nP = rows.length;

  const precipitation = Array.from({ length: nT }, () => Array(nP).fill(0));
  const temperature_2m = Array.from({ length: nT }, () => Array(nP).fill(0));
  const wind_speed_10m = Array.from({ length: nT }, () => Array(nP).fill(0));
  const wind_direction_10m = Array.from({ length: nT }, () => Array(nP).fill(0));

  rows.forEach((row, pi) => {
    const h = row.hourly || {};
    for (let ti = 0; ti < nT; ti++) {
      precipitation[ti][pi] = Number(h.precipitation?.[ti] ?? 0);
      temperature_2m[ti][pi] = Number(h.temperature_2m?.[ti] ?? 0);
      wind_speed_10m[ti][pi] = Number(h.wind_speed_10m?.[ti] ?? 0);
      wind_direction_10m[ti][pi] = Number(h.wind_direction_10m?.[ti] ?? 0);
    }
  });

  /** @type {ForecastGrid} */
  const grid = {
    times,
    timesUnix,
    flatLat: GRID.flatLat,
    flatLon: GRID.flatLon,
    nLat: GRID.nLat,
    nLon: GRID.nLon,
    precipitation,
    temperature_2m,
    wind_speed_10m,
    wind_direction_10m,
    fetchedAt: Date.now(),
  };
  writeCache(grid);
  return grid;
}

export async function fetchPointGlance(lat, lon) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    hourly: 'precipitation,precipitation_probability,weather_code',
    forecast_days: '2',
    timezone: 'Asia/Jerusalem',
    models: 'ecmwf_ifs',
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
  if (!res.ok) throw new Error(`Open-Meteo glance ${res.status}`);
  const data = await res.json();
  const times = data.hourly?.time || [];
  const precip = data.hourly?.precipitation || [];
  const now = Date.now();
  let raining = false;
  let soonIdx = -1;
  for (let i = 0; i < times.length; i++) {
    const t = new Date(times[i]).getTime();
    if (t < now - 30 * 60 * 1000) continue;
    if (t > now + 36 * 60 * 60 * 1000) break;
    const p = Number(precip[i] || 0);
    if (p >= 0.2) {
      if (t <= now + 45 * 60 * 1000) raining = true;
      if (soonIdx < 0) soonIdx = i;
    }
  }
  if (raining) return { mode: 'raining', precipMm: precip[soonIdx], whenIso: times[soonIdx] };
  if (soonIdx >= 0) {
    return { mode: 'soon', whenIso: times[soonIdx], precipMm: precip[soonIdx] };
  }
  return { mode: 'dry' };
}

/** Bilinear sample of a time-slice field at lon/lat. */
export function sampleField(grid, field, timeIndex, lon, lat) {
  const vals = grid[field]?.[timeIndex];
  if (!vals) return 0;
  const { nLon, nLat, flatLat, flatLon } = grid;
  // grid is row-major over lat then lon
  const lat0 = flatLat[0];
  const lon0 = flatLon[0];
  const dLat = flatLat[nLon] - flatLat[0];
  const dLon = flatLon[1] - flatLon[0];
  const fy = (lat - lat0) / dLat;
  const fx = (lon - lon0) / dLon;
  const y0 = Math.floor(fy);
  const x0 = Math.floor(fx);
  if (y0 < 0 || x0 < 0 || y0 >= nLat - 1 || x0 >= nLon - 1) {
    const yi = Math.min(nLat - 1, Math.max(0, Math.round(fy)));
    const xi = Math.min(nLon - 1, Math.max(0, Math.round(fx)));
    return vals[yi * nLon + xi] ?? 0;
  }
  const ty = fy - y0;
  const tx = fx - x0;
  const i00 = y0 * nLon + x0;
  const i10 = y0 * nLon + x0 + 1;
  const i01 = (y0 + 1) * nLon + x0;
  const i11 = (y0 + 1) * nLon + x0 + 1;
  const a = vals[i00] * (1 - tx) + vals[i10] * tx;
  const b = vals[i01] * (1 - tx) + vals[i11] * tx;
  return a * (1 - ty) + b * ty;
}
